import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";
import UAParser, { IResult } from "ua-parser-js";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { clientWindow, isServerRendering } from "./dom";
import { CustomRequestInit } from "@/typings";
import { isInApp } from "./is";
import { getClientInfo } from "./get-app-info";
import { getDeviceId, setDeviceId } from "./storage";

/**
 * 获取浏览器指纹 ID
 * @returns Promise<string> visitorId
 */
export const getFingerprint = async (): Promise<string> => {
  if (!clientWindow) return "";
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch {
    return "";
  }
};

export async function generateFingerprint() {
  const uid = await getDeviceId();
  if (uid) return uid;
  const newUuid = uuidv4();
  // 收集一些环境信息
  const userAgent = typeof window !== "undefined" ? navigator.userAgent : "";
  const platform = typeof window !== "undefined" ? navigator.platform : "";

  // 将收集到的信息拼接起来
  const dataToHash = `${userAgent || ""}` + platform + newUuid;

  // 使用加密库生成哈希值
  const hash = CryptoJS.SHA256(dataToHash);
  setDeviceId(hash.toString());
  return hash.toString();
}

export function getOS() {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  if (platform.indexOf("Win") === 0) {
    return "Windows";
  } else if (platform.indexOf("Mac") === 0) {
    return "Mac OS";
  } else if (platform.indexOf("Linux") === 0) {
    return "Linux";
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "iOS";
  } else if (/Android/i.test(userAgent)) {
    return "Android";
  } else {
    return "Unknown";
  }
}

export function getDeviceBrandFromPlatform() {
  const platform = navigator.platform;
  if (platform === "MacIntel") {
    return "Apple";
  } else if (platform === "Win32" || platform === "Win64") {
    return "Microsoft";
  } else if (platform === "Linux") {
    return "Linux"; // 这里的Linux系统可能对应多种设备品牌，但无法进一步细分
  } else if (platform === "iPhone") {
    return "Apple";
  } else if (platform === "iPad") {
    return "Apple";
  } else if (platform === "iPod") {
    return "Apple";
  } else if (platform === "Android") {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Samsung") !== -1) {
      return "Samsung";
    } else if (userAgent.indexOf("Huawei") !== -1) {
      return "Huawei";
    } else if (userAgent.indexOf("Xiaomi") !== -1) {
      return "Xiaomi";
    } else {
      return "Android"; // 如果无法确定具体品牌，返回Android作为统称
    }
  } else {
    return "Unknown";
  }
}

export const getDevice = () => {
  const userAgent = navigator.userAgent;
  const match = userAgent.match(/\(([^)]+)\)/);
  return match ? match[1] : getDeviceBrandFromPlatform();
};

export const getTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// node服务端获取用户的设备信息
export const getDeviceByHeader = (userAgentString: string) => {
  // 初始化 UAParser
  const parser = new UAParser();
  // 设置 User-Agent 字符串
  const result: IResult = parser.setUA(userAgentString).getResult();
  const userAgentObject = {
    os: result?.os?.name || "",
    device: `${result?.device?.vendor || ""} ${result?.device?.model || ""}; ${result?.os?.name || ""} ${result?.os?.version || ""}`,
    // device_brand: result.device?.vendor || "",
  };
  return userAgentObject;
};

export function ua() {
  return new UAParser(navigator.userAgent);
}

/**
 * 获取网络类型
 * @returns 网络类型字符串
 */
export function networkType(): string {
  if ("connection" in navigator) {
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;
    if (!connection) return "";

    const type = connection.type;
    const effectiveType = connection.effectiveType;
    if (type === "wifi" || effectiveType === "wifi") {
      return "wifi";
    }
    if (
      type === "cellular" ||
      ["2g", "3g", "4g", "5g"].includes(effectiveType)
    ) {
      return effectiveType;
    }
    return "unk";
  }
  return "";
}

/**
 * 构建App客户端信息
 * @returns 客户端信息对象
 */
async function buildAppClientInfo() {
  const clientInfo = await getClientInfo();
  return {
    client_version: clientInfo?.client_version ?? "0.0.0",
    os: clientInfo?.os ?? "",
    build_number: clientInfo?.build_number ?? "0",
    in_app: true,
    device: clientInfo?.device ?? "",
    device_id: clientInfo?.device_id ?? "",
    inviter: "",
  };
}

/**
 * 构建浏览器客户端信息
 * @param params 请求参数
 * @returns 客户端信息对象
 */
async function buildBrowserClientInfo(params?: CustomRequestInit) {
  let deviceHeaders = null;
  if (isServerRendering) {
    deviceHeaders = {
      ...(getDeviceByHeader(params?.headers?.["userAgent"] as string) || ""),
    };
  } else {
    const device_id = await getFingerprint();
    deviceHeaders = {
      os: getOS(),
      device: getDevice(),
      device_id,
    };
  }
  return {
    ...deviceHeaders,
    build_number: "10000",
    in_app: false,
    client_version: "0.0.0",
    inviter: "",
  };
}

/**
 * 构建客户端信息
 * @param params 请求参数
 * @returns 客户端信息对象
 */
export async function buildClientInfo(params?: CustomRequestInit) {
  const inApp = isInApp();
  if (inApp) {
    return await buildAppClientInfo();
  }
  // yoofinds 不存在 (超时或非App环境)
  return await buildBrowserClientInfo(params);
}
